/**
 * Small markdown-rendered field with Edit/Preview toggle.
 *
 * - In Edit mode: behaves like a textarea.
 * - In Preview mode: renders GitHub-flavoured-style markdown.
 * - Auto-switches to Preview when `value` has content and the field is
 *   not focused; Edit while focused or if value is empty.
 *
 * `readOnly` (Session 68 pt5) — locks the field to Preview mode and
 *   hides the Edit tab + the "Markdown supported" hint. Used on the
 *   client-side meetings tab so Stanford (and other clients) can
 *   scroll the transcript / summary without seeing controls they
 *   can't action. The underlying textarea is also dropped from the
 *   tree so screen readers don't expose an editable shadow.
 */
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { PencilSimple, Eye } from "@phosphor-icons/react";

export default function MarkdownField({ value, onChange, placeholder, rows = 4, testId, monospace = false, readOnly = false }) {
  const [mode, setMode] = useState(value ? "preview" : (readOnly ? "preview" : "edit"));
  const effectiveMode = readOnly ? "preview" : mode;

  return (
    <div data-testid={testId}>
      {!readOnly && (
        <div className="flex items-center gap-1 mb-1">
          <button
            type="button"
            onClick={() => setMode("edit")}
            className={`px-2 h-6 text-[10px] font-bold uppercase tracking-wider border-b-2 inline-flex items-center gap-1 ${effectiveMode === "edit" ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-[#A3A3A3]"}`}
            data-testid={`${testId}-edit`}
          >
            <PencilSimple size={11} /> Edit
          </button>
          <button
            type="button"
            onClick={() => setMode("preview")}
            className={`px-2 h-6 text-[10px] font-bold uppercase tracking-wider border-b-2 inline-flex items-center gap-1 ${effectiveMode === "preview" ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-[#A3A3A3]"}`}
            data-testid={`${testId}-preview`}
          >
            <Eye size={11} /> Preview
          </button>
          <span className="ml-auto text-[10px] text-[#A3A3A3]">Markdown supported</span>
        </div>
      )}
      {effectiveMode === "edit" ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          className={`w-full px-3 py-2 border border-[#E5E5E5] text-sm ${monospace ? "font-mono" : ""}`}
          data-testid={`${testId}-textarea`}
        />
      ) : (
        <div
          // The transcript/summary panes can be very long. We cap the
          // height and let the user scroll within the preview pane —
          // this matches Mike's specific "they should be able to read
          // and scroll through the meeting transcripts" requirement
          // without forcing the rest of the page to scroll.
          style={{ maxHeight: rows >= 7 ? "420px" : "260px" }}
          className={`min-h-[80px] px-3 py-2 border border-[#E5E5E5] bg-[#FAFAFA] text-sm prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-headings:mb-1 prose-headings:mt-2 overflow-y-auto ${monospace ? "font-mono whitespace-pre-wrap" : ""}`}
          data-testid={`${testId}-rendered`}
        >
          {value ? (
            <ReactMarkdown>{value}</ReactMarkdown>
          ) : (
            <p className="text-[#A3A3A3] italic text-xs">{placeholder || "Nothing to preview yet."}</p>
          )}
        </div>
      )}
    </div>
  );
}
